import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Link,
    Typography,
    useScrollTrigger,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ELinks } from '@sharedInterfaces/globalEnums';
import getEmployee from '@src/APIs/graphQl/Employee/getEmployee';
import type { CustomGraphQLError } from '@src/APIs/graphQl/graphQL';
import EmployeeTalksWidget from '@src/App/Components/_UnderConstruction/Widgets/EmployeeTalksWidget';
import InnerLayout from '@src/App/Components/Core/Layouts/InnerLayout/InnerLayout';
import MvpSubNav, { type IMvpQuickAction } from '@src/App/Components/Core/MvpSubNav/MvpSubNav';
import LoadingBox from '@src/App/Components/General/LoadingBox/LoadingBox';
import NotificationWidget from '@src/App/Components/Widgets/NotificationWidget';
import { EIconCategory, getAppIcon } from '@src/helper/iconRegistry';
import type Employee from '@src/Objects/Employee';
import type { AppState } from '@store/store';
import { X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import MyDevCard from '../MyDevCard/MyDevCard';
import MyProfileCard from '../MyProfileCard';
import ProfileCard from '../ProfileCard/ProfileCard';
import ProfileAboutSection from './widgets/ProfileAboutSection';
import ProfileAvailabilitySection from './widgets/ProfileAvailabilitySection';
import ProfileCertificationsSection from './widgets/ProfileCertificationsSection';
import ProfileCompetenciesSection from './widgets/ProfileCompetenciesSection';
import ProfileContactSection from './widgets/ProfileContactSection';
import ProfileProjectsSection from './widgets/ProfileProjectsSection';
import ProfileSkillsSection from './widgets/ProfileSkillsSection';
import ProfileSuperPowersSection from './widgets/ProfileSuperPowersSection';

interface MvpEmployeePageProps {
    entity?: Employee;
    inDialog?: boolean;
    isPreview?: boolean;
}

function MvpEmployeePage({ entity, inDialog, isPreview }: MvpEmployeePageProps): JSX.Element;
function MvpEmployeePage({ entity, inDialog, isPreview }: MvpEmployeePageProps): JSX.Element {
    const { id } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const [loadingError, setLoadingError] = useState<CustomGraphQLError | null>(null);
    const [showProfilePreview, setShowProfilePreview] = useState(false);

    const ownId = useSelector((state: AppState) => state.employee.id);
    const entityId = entity ? entity.id : id ? Number.parseInt(id) : ownId;
    const entityState = useSelector((state: AppState) => state.employees.find((a) => a.id === entityId));
    const OUs = useSelector((state: AppState) => state.OUs);

    const itsMe = isPreview ? false : entityId === ownId;

    useEffect(() => {
        if (inDialog) return;
        getEmployee(entityId).catch((ex) => {
            setLoadingError(ex);
        });
    }, [entityId, inDialog]);

    const OU = entityState ? OUs.find((o) => o.employees.includes(entityState.id)) : undefined;

    let content = null;

    if (entityState) {
        if (itsMe) {
            content = (
                <>
                    <Box sx={{ width: '100%', mb: 3 }}>
                        <MyProfileCard employee={entityState} orgUnit={OU} />
                    </Box>
                    <MyDevCardWithWidgets employee={entityState} />
                </>
            );
        } else {
            content = <OtherEmployeeView employee={entityState} orgUnit={OU} inDialog={inDialog} />;
        }
    }

    if (loadingError) {
        return (
            <InnerLayout title="Fehler">
                <Typography>{loadingError.message}</Typography>
            </InnerLayout>
        );
    }

    if (!entityState) {
        return (
            <InnerLayout title="Laden...">
                <LoadingBox />
            </InnerLayout>
        );
    }

    const quickActions: IMvpQuickAction[] = [
        {
            id: 'edit-profile',
            label: 'Profil bearbeiten',
            icon: getAppIcon(EIconCategory.EDIT),
            onClick: () => navigate(`/${ELinks.PROFILE_EDIT}`),
            variant: 'primary',
        },
        {
            id: 'profile-preview',
            label: 'Profil Vorschau',
            icon: getAppIcon(EIconCategory.PROFILE_PREVIEW),
            onClick: () => setShowProfilePreview(true),
        },
    ];

    return (
        <>
            {!inDialog && (
                <MvpSubNav
                    currentPageTitle={itsMe ? 'Mein Profil' : entityState?.firstName + ' ' + entityState?.lastName}
                    quickActions={itsMe ? quickActions : []}
                    shareEntity={entityState}
                />
            )}
            <InnerLayout hideHeader>{content}</InnerLayout>

            {/* Profile Preview Dialog */}
            <Dialog
                open={showProfilePreview}
                onClose={() => setShowProfilePreview(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        maxHeight: '90vh',
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        pb: 1,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                >
                    Profil Vorschau - So sehen dich andere
                    <IconButton
                        onClick={() => setShowProfilePreview(false)}
                        size="small"
                        sx={{
                            color: theme.palette.text.secondary,
                            '&:hover': {
                                bgcolor: theme.palette.action.hover,
                            },
                        }}
                    >
                        <X size={20} />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 0, overflow: 'auto', maxHeight: '80vh' }}>
                    {entityState && <MvpEmployeePage entity={entityState} inDialog={true} isPreview={true} />}
                </DialogContent>
            </Dialog>
        </>
    );
}

function OtherEmployeeView({
    employee,
    orgUnit,
    inDialog,
}: {
    employee: Employee;
    orgUnit?: { id: number; title: string };
    inDialog?: boolean;
}) {
    const theme = useTheme();
    const [activeSection, setActiveSection] = useState('about');

    const trigger = useScrollTrigger({
        disableHysteresis: true,
        threshold: 50,
        target: inDialog ? (document.querySelector('.MuiDialog-paper') as Element) : undefined,
    });

    useEffect(() => {
        const handleScroll = () => {
            const sections = [
                'about',
                'superpowers',
                'skills',
                'certificates',
                'projects',
                'competences',
                'availability',
                'contact',
            ];
            const scrollContainer = inDialog ? document.querySelector('.MuiDialog-paper') : window;
            const scrollTop = inDialog ? (scrollContainer as Element)?.scrollTop || 0 : window.scrollY;
            const scrollPosition = scrollTop + 200;

            for (let i = sections.length - 1; i >= 0; i--) {
                const element = document.getElementById(sections[i]);
                if (element && element.offsetTop <= scrollPosition) {
                    setActiveSection(sections[i]);
                    break;
                }
            }
        };

        const scrollContainer = inDialog ? document.querySelector('.MuiDialog-paper') : window;
        scrollContainer?.addEventListener('scroll', handleScroll);
        return () => scrollContainer?.removeEventListener('scroll', handleScroll);
    }, [inDialog]);

    const sections = [
        { id: 'about', label: 'Info', icon: getAppIcon(EIconCategory.INFO) },
        { id: 'superpowers', label: 'SuperPowers', icon: getAppIcon(EIconCategory.SUPERPOWERS) },
        { id: 'skills', label: 'Skills', icon: getAppIcon(EIconCategory.SKILLS) },
        { id: 'certificates', label: 'Zertifikate', icon: getAppIcon(EIconCategory.CERTIFICATES) },
        { id: 'projects', label: 'Projekte', icon: getAppIcon(EIconCategory.PROJECTS) },
        { id: 'competences', label: 'Kompetenzen', icon: getAppIcon(EIconCategory.COMPETENCES) },
        { id: 'availability', label: 'Verfügbarkeit', icon: getAppIcon(EIconCategory.AVAILABILITY) },
        { id: 'contact', label: 'Kontakt', icon: getAppIcon(EIconCategory.CONTACT) },
    ];

    return (
        <>
            <ProfileCard employee={employee} itsMe={false} orgUnit={orgUnit} />

            <Box
                sx={{
                    position: 'sticky',
                    top: 0,
                    bgcolor: trigger ? alpha(theme.palette.background.paper, 0.8) : 'transparent',
                    backdropFilter: trigger ? 'blur(12px)' : 'none',
                    borderBottom: trigger ? `1px solid ${alpha(theme.palette.divider, 0.3)}` : 'none',
                    zIndex: 1100,
                    py: { xs: 1.5, md: 2 },
                    mt: 1,
                    mb: 3,
                    display: 'flex',
                    justifyContent: { xs: 'flex-start', md: 'center' },
                    gap: { xs: 1.5, md: 3 },
                    flexWrap: { xs: 'nowrap', md: 'wrap' },
                    overflowX: { xs: 'auto', md: 'visible' },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&::-webkit-scrollbar': {
                        display: { xs: 'block', md: 'none' },
                        height: '4px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: alpha(theme.palette.divider, 0.1),
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: alpha(theme.palette.primary.main, 0.3),
                        borderRadius: '2px',
                    },
                    scrollbarWidth: { xs: 'thin', md: 'none' },
                    scrollbarColor: `${alpha(theme.palette.primary.main, 0.3)} ${alpha(theme.palette.divider, 0.1)}`,
                    boxShadow: trigger ? `0 2px 8px ${alpha(theme.palette.common.black, 0.1)}` : 'none',
                }}
            >
                {sections.map((section) => (
                    <Link
                        key={section.id}
                        component="button"
                        onClick={() => {
                            setActiveSection(section.id);
                            document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: { xs: 0.8, md: 1 },
                            color:
                                activeSection === section.id
                                    ? theme.palette.primary.main
                                    : theme.palette.text.secondary,
                            fontWeight: activeSection === section.id ? 500 : 400,
                            textDecoration: 'none',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            border: 'none',
                            background: activeSection === section.id ? alpha(theme.palette.primary.main, 0.1) : 'none',
                            borderRadius: 2,
                            px: { xs: 1, md: 1.5 },
                            py: { xs: 0.5, md: 0.75 },
                            fontSize: { xs: '0.75rem', md: '0.875rem' },
                            minWidth: 'fit-content',
                            whiteSpace: 'nowrap',
                            '&:hover': {
                                color: theme.palette.primary.main,
                                background: alpha(theme.palette.primary.main, 0.08),
                            },
                        }}
                    >
                        {section.icon}
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                            {section.label}
                        </Box>
                    </Link>
                ))}
            </Box>

            <Box id="about" sx={{ mb: 3, scrollMarginTop: '100px' }}>
                <ProfileAboutSection employee={employee} />
            </Box>

            <Box id="superpowers" sx={{ mb: 3, scrollMarginTop: '100px' }}>
                <ProfileSuperPowersSection />
            </Box>

            <Box id="skills" sx={{ mb: 3, scrollMarginTop: '100px' }}>
                <ProfileSkillsSection employee={employee} />
            </Box>

            <Box id="certificates" sx={{ mb: 3, scrollMarginTop: '100px' }}>
                <ProfileCertificationsSection employee={employee} />
            </Box>

            <Box id="projects" sx={{ mb: 3, scrollMarginTop: '100px' }}>
                <ProfileProjectsSection employee={employee} />
            </Box>

            <Box id="competences" sx={{ mb: 3, scrollMarginTop: '100px' }}>
                <ProfileCompetenciesSection employee={employee} />
            </Box>

            <Box id="availability" sx={{ mb: 3, scrollMarginTop: '100px' }}>
                <ProfileAvailabilitySection employee={employee} />
            </Box>

            <Box id="contact" sx={{ mb: 3, scrollMarginTop: '100px' }}>
                <ProfileContactSection employee={employee} />
            </Box>
        </>
    );
}

function MyDevCardWithWidgets({ employee }: { employee: Employee }) {
    return (
        <>
            <Box sx={{ width: '100%', mb: 3 }}>
                <MyDevCard employee={employee} />
            </Box>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                    <NotificationWidget />
                </Box>
                <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                    <EmployeeTalksWidget />
                </Box>
            </Box>
        </>
    );
}

export default MvpEmployeePage;
export { MvpEmployeePage };
